import React, { useCallback, useEffect, useState } from 'react';
import { describeDecisionError } from '../../../utils/loadError';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ACTIONS_COL_WIDTH,
  Alert,
  Badge,
  Button,
  Card,
  Col,
  DataTable,
  DecisionFeedback,
  type DecisionResult,
  PageHeader,
  Row,
  RowActions,
  Tab,
  Tabs,
  StatusBadge,
} from '@/components/ui';
import type { DataTableColumn } from '@/components/ui';
import { usePermissions } from '../../../hooks/usePermissions';
import { ExpiryDate, ExpiryIndicator } from '../../../components/ExpiryIndicator';
import { areasService } from '../../../services/areasService';
import {
  RESOURCE_TYPE_LABELS,
  Vendor,
  VendorCreatePayload,
  VendorResource,
  groupResourcesByType,
  vendorService,
} from '../../../services/vendorService';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { notifyApprovalsChanged } from '../../../components/awaitingApprovalsCount';
import ResourceRatesCell from '../components/ResourceRatesCell';
import { BundleComponentsList, BundleComponentsToggle } from '../components/BundleContents';
import PossibleMatches from '../components/PossibleMatches';
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown';
import VendorModal from '../components/VendorModal';
import AddVendorToAreaModal from '../components/AddVendorToAreaModal';
import ResourceModal from '../components/ResourceModal';
import RateModal from '../components/RateModal';
import VendorDocumentsPanel from '../components/VendorDocumentsPanel';
import VendorHistoryTab from '../components/VendorHistoryTab';
import { enteredByLabel, shortDate } from '../utils/format';
import { canOfferAddToMyArea, vendorIsInCallerArea } from '../utils/callerAreas';
import { complianceBannerItems, complianceBannerText } from '../utils/complianceBanner';
import { isAwaitingFirstRate } from '../utils/rateDisplay';
import { confirmAction } from '@/components/ui';

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="tw-mb-3">
    <div className="tw-text-sm tw-text-content-muted">{label}</div>
    <div className="tw-text-content">{children}</div>
  </div>
);

/**
 * Tabs addressable via `?tab=`. Anything else falls back to the first tab. Every tab rendered
 * below must be listed here: History was missing (H10), so selecting it - or opening
 * ?tab=history - silently showed Resources & Rates instead.
 */
export const VENDOR_DETAIL_TABS = ['resources', 'documents', 'history'];

const VendorDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Which tab is open lives in the URL so other screens can link straight to it - the Home
   * compliance widget drills through to Documents for a certificate and to Resources & Rates
   * for a machine, and landing on the wrong tab makes the link useless.
   *
   * Replace rather than push: switching tabs is not a navigation step someone wants to undo
   * with the back button.
   */
  const activeTab = VENDOR_DETAIL_TABS.includes(searchParams.get('tab') ?? '')
    ? (searchParams.get('tab') as string)
    : 'resources';

  const handleTabSelect = (key: string | null) => {
    if (!key) return;
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    setSearchParams(next, { replace: true });
  };
  const { hasPermission } = usePermissions();

  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [activateResult, setActivateResult] = useState<DecisionResult | null>(null);

  const [showVendorModal, setShowVendorModal] = useState(false);
  const [showAddToArea, setShowAddToArea] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState(false);
  /** Narrows the resource list; replaces the old Service Areas tab. */
  const [resourceAreaIds, setResourceAreaIds] = useState<number[]>([]);
  const [editingResource, setEditingResource] = useState<VendorResource | null>(null);
  const [rateTarget, setRateTarget] = useState<VendorResource | null>(null);
  /** Bundles whose contents are expanded in place (M27). */
  const [expandedBundles, setExpandedBundles] = useState<Set<number>>(new Set());

  const canManageResources = hasPermission('heMaster:resource_manage');
  const canEditRates = hasPermission('heMaster:rate_edit');
  const canActivate = hasPermission('heMaster:vendor_activate');
  const canManageDocs = hasPermission('heMaster:vendor_document_manage');
  const canEditVendor = hasPermission('heMaster:vendor_edit');
  const canAddArea =
    hasPermission('heMaster:vendor_add_area') || hasPermission('heMaster:vendor_edit');

  const [callerAreas, setCallerAreas] = useState<Array<{ id: number; name: string }>>([]);

  useEffect(() => {
    areasService
      .getServiceAreas()
      .then((areas) => setCallerAreas((areas ?? []).map((a) => ({ id: a.id, name: a.name }))))
      .catch(() => setCallerAreas([]));
  }, []);

  const load = useCallback(async (options?: { silent?: boolean }) => {
    if (!id) return;
    try {
      if (!options?.silent) setLoading(true);
      setError(null);
      setVendor(await vendorService.getVendorById(parseInt(id, 10)));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load vendor';
      // A silent refresh (e.g. after adding a rate) must not unmount the page — that would
      // close the rates modal. Surface the failure on the action banner instead.
      if (options?.silent) setActionError(message);
      else setError(message);
    } finally {
      if (!options?.silent) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useDocumentTitle(vendor?.name ?? 'Vendor');

  const toggleBundle = (resourceId: number) =>
    setExpandedBundles((prev) => {
      const next = new Set(prev);
      if (next.has(resourceId)) next.delete(resourceId);
      else next.add(resourceId);
      return next;
    });

  // V3-016: Activate Vendor always asks first (the app dialog, blue button, as on Vendor
  // Approvals) and always shows its result. No email goes to the vendor on activation.
  const handleActivate = async () => {
    if (!vendor) return;
    const reactivate = vendor.status === 'INACTIVE';
    if (
      !(await confirmAction({
        title: `${reactivate ? 'Reactivate' : 'Activate'} "${vendor.name}"?`,
        body: 'It becomes available for work in its service areas.',
        confirmLabel: reactivate ? 'Reactivate' : 'Activate',
      }))
    ) {
      return;
    }
    await runActivate(vendor, reactivate);
  };

  const runActivate = async (target: Vendor, reactivate: boolean) => {
    try {
      setActionError(null);
      setActivateResult(null);
      await vendorService.activateVendor(target.id);
      notifyApprovalsChanged();
      setActivateResult({ tone: 'success', message: `${target.name} ${reactivate ? 'reactivated' : 'activated'} – now Active` });
      await load();
    } catch (err) {
      // Coverage-lapse refusals come back as 409 with a message written to be read by a person;
      // a 5xx gets the friendly line with Retry (V3-015).
      setActivateResult({
        tone: 'error',
        message: describeDecisionError(err, reactivate ? 'reactivate the vendor' : 'activate the vendor'),
        onRetry: () => void runActivate(target, reactivate),
      });
    }
  };

  const handleDeactivate = async () => {
    if (!vendor) return;
    // Destructive and easy to hit by accident, so confirm first — same contract as the other
    // destructive actions on this feature (RateModal, VendorDocumentsPanel) and as documented
    // on RowActions.
    if (
      !(await confirmAction({ title: `Deactivate "${vendor.name}"?`, body: 'They will stop appearing as an available vendor. You can reactivate them from this page.', confirmLabel: 'Deactivate', destructive: true }))
    ) {
      return;
    }
    try {
      setActionError(null);
      await vendorService.deactivateVendor(vendor.id);
      await load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to deactivate vendor');
    }
  };

  const handleSaveVendor = async (data: VendorCreatePayload) => {
    if (!vendor) return;
    await vendorService.updateVendor(vendor.id, data);
    setShowVendorModal(false);
    await load();
  };

  if (loading) return <div className="tw-p-4">Loading vendor…</div>;
  if (error) return <div className="tw-p-4 tw-text-danger">Error: {error}</div>;
  if (!vendor) return <div className="tw-p-4">Vendor not found.</div>;

  const isActive = vendor.status === 'ACTIVE';
  const callerAreaIds = callerAreas.map((a) => a.id);
  const inCallerArea = vendorIsInCallerArea(vendor, callerAreaIds);
  // V2-044 (M14a): the banner and the header action are both for a vendor in NONE of the
  // caller's areas. A vendor already in one of them is theirs to use.
  const canAdopt = canAddArea && canOfferAddToMyArea(vendor, callerAreaIds);
  const showAdoptBanner = canAdopt;
  // M14b: what still blocks activation, computed by the server from the same rule it enforces.
  const activationBlockers = vendor.activationBlockers ?? [];
  const complianceItems = complianceBannerItems(vendor.compliance, vendor.resources ?? []);

  const vendorAreaOptions = (vendor.serviceAreas ?? []).map((sa) => sa.serviceArea);

  const visibleResources = resourceAreaIds.length
    ? (vendor.resources ?? []).filter((r) =>
        r.serviceAreas?.some((sa) => resourceAreaIds.includes(sa.serviceAreaId))
      )
    : vendor.resources ?? [];

  const resourceColumns: DataTableColumn<VendorResource>[] = [
    {
      key: 'name',
      header: 'Resource',
      mobile: 'title',
      cell: (r) =>
        r.resourceType === 'BUNDLE' ? (
          <span className="tw-inline-flex tw-flex-col tw-gap-0.5">
            <span>{r.name}</span>
            <BundleComponentsToggle
              resource={r}
              expanded={expandedBundles.has(r.id)}
              onToggle={() => toggleBundle(r.id)}
            />
          </span>
        ) : (
          r.name
        ),
    },
    {
      key: 'resourceType',
      header: 'Type',
      mobile: 'subtitle',
      cell: (r) => (
        <span>
          {RESOURCE_TYPE_LABELS[r.resourceType]}
          {r.isFixedPrice && (
            <Badge bg="secondary" className="tw-ml-2">
              Fixed price
            </Badge>
          )}
        </span>
      ),
    },
    {
      key: 'vehicleType',
      mobile: 'hidden',
      // M17a: what the value selects is the fuel surcharge class.
      header: 'Fuel Class',
      cell: (r) => r.vehicleType ?? '–',
    },
    {
      key: 'vehicleInsuranceExpDate',
      mobile: 'hidden',
      // "Vehicle Insurance", not "Insurance": the vendor-level liability cover is also on this
      // page, and the two sharing one word made it impossible to tell which was which.
      header: 'Vehicle Insurance',
      cell: (r) => {
        // Not every resource carries a policy — an attachment or a material does not — so a
        // blank here is normal rather than missing data, and must not look like a warning.
        if (!r.requiresVehicleInsurance) {
          return <span className="tw-text-content-muted">Not required</span>;
        }
        if (!r.vehicleInsuranceExpDate) {
          return (
            <span className="tw-text-danger tw-inline-flex tw-flex-wrap tw-items-center tw-gap-2">
              <span>
                <i className="bi bi-exclamation-triangle-fill tw-mr-1" />
                Missing
              </span>
              {r.vehicleInsuranceFileUrl && (
                <a
                  href={r.vehicleInsuranceFileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw-text-primary tw-text-sm tw-whitespace-nowrap"
                  onClick={(e) => e.stopPropagation()}
                >
                  <i className="bi bi-paperclip tw-mr-1" />
                  {r.vehicleInsuranceFileName || 'Certificate'}
                </a>
              )}
            </span>
          );
        }
        const expired = new Date(r.vehicleInsuranceExpDate) < new Date();
        // Flag only: an expired machine warns here and never blocks the vendor (decision 2).
        return (
          <span className="tw-inline-flex tw-flex-wrap tw-items-center tw-gap-2">
            <ExpiryDate value={r.vehicleInsuranceExpDate} expired={expired} />
            {r.vehicleInsuranceFileUrl && (
              <a
                href={r.vehicleInsuranceFileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="tw-text-sm tw-whitespace-nowrap"
                onClick={(e) => e.stopPropagation()}
              >
                <i className="bi bi-paperclip tw-mr-1" />
                {r.vehicleInsuranceFileName || 'Certificate'}
              </a>
            )}
          </span>
        );
      },
    },
    {
      key: 'customerUnitNumber',
      mobile: 'hidden',
      header: 'Vendor Unit #',
      cell: (r) => r.customerUnitNumber || '–',
    },
    { key: 'unit', header: 'Units of Measure', cell: (r) => r.unit ?? '–' },
    {
      key: 'serviceAreas',
      mobile: 'hidden',
      header: 'Areas',
      cell: (r) =>
        r.serviceAreas?.length
          ? r.serviceAreas.map((sa) => sa.serviceArea.name).join(', ')
          : '–',
    },
    {
      key: 'rates',
      header: 'Rates',
      // M19: the rate in effect today, with future rates labelled rather than shown as current.
      cell: (r) => <ResourceRatesCell rates={r.rates} />,
    },
    {
      key: 'isActive',
      header: 'Status',
      mobile: 'trailing',
      cell: (r) => (
        isAwaitingFirstRate(r) ? (
          <StatusBadge status="pending" label="Pending" variant="warning" />
        ) : r.isActive ? (
          <StatusBadge status="active" label="Active" variant="success" />
        ) : (
          <StatusBadge status="inactive" label="Inactive" variant="secondary" />
        )
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      width: ACTIONS_COL_WIDTH,
      // `trailing` puts these in the card's top-right on a phone. Without it DataTable
      // defaults to `meta`, which renders them in the field grid under a blank label.
      mobile: 'trailing',
      cell: (r) => (
        <RowActions
          actions={[
            {
              kind: 'custom',
              icon: 'bi-cash-stack',
              variant: 'outline-secondary',
              label: 'Rates',
              onClick: () => setRateTarget(r),
              hidden: !canEditRates || !inCallerArea,
            },
            {
              kind: 'edit',
              label: 'Edit Resource',
              onClick: () => {
                setEditingResource(r);
                setShowResourceModal(true);
              },
              hidden: !canManageResources || !inCallerArea,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="vendor-detail-page tw-p-4">
      <PageHeader
        title={vendor.name}
        actions={[
          {
            key: 'back',
            label: 'Back to vendors',
            icon: 'bi-arrow-left',
            intent: 'secondary',
            onClick: () => navigate('/vendors'),
          },
          // INACTIVE belongs here as well as PENDING: the server's activateVendor happily
          // transitions INACTIVE -> ACTIVE, but gating this button on PENDING alone left a
          // deactivated vendor with no route back — /he-admin only lists PENDING vendors.
          ...(canActivate && inCallerArea && (vendor.status === 'PENDING' || vendor.status === 'INACTIVE')
            ? [
                {
                  key: 'activate',
                  label: vendor.status === 'INACTIVE' ? 'Reactivate Vendor' : 'Activate Vendor',
                  icon: 'bi-check-circle',
                  intent: 'primary' as const,
                  onClick: handleActivate,
                  // M14b: disabled until the requirements are met; the reasons are in the
                  // tooltip and in the banner below.
                  disabled: activationBlockers.length > 0,
                  title: activationBlockers.length
                    ? `Cannot activate yet: ${activationBlockers.join('; ')}`
                    : undefined,
                },
              ]
            : []),
          ...(canActivate && inCallerArea && isActive
            ? [
                {
                  key: 'deactivate',
                  label: 'Deactivate',
                  icon: 'bi-x-circle',
                  intent: 'danger' as const,
                  onClick: handleDeactivate,
                },
              ]
            : []),
          ...(canAdopt
            ? [
                {
                  key: 'add-area',
                  label: 'Add to my area',
                  icon: 'bi-geo-alt',
                  intent: 'primary' as const,
                  onClick: () => setShowAddToArea(true),
                },
              ]
            : []),
          ...(canEditVendor && inCallerArea
            ? [
                {
                  key: 'edit',
                  label: 'Edit Vendor',
                  icon: 'bi-pencil',
                  // Activate/Deactivate is the page's primary action; two solid blue buttons
                  // side by side give no read on which one matters.
                  intent: 'secondary' as const,
                  // The only route to changing which service areas a vendor operates in.
                  onClick: () => setShowVendorModal(true),
                },
              ]
            : []),
        ]}
      />

      {actionError && (
        <Alert variant="danger" className="tw-mb-3">
          {actionError}
        </Alert>
      )}
      {activateResult && <DecisionFeedback result={activateResult} className="tw-mb-3" />}

      {showAdoptBanner && (
        <Alert variant="info" className="tw-mb-3">
          This vendor is set up in another service area. Add it to yours to use it here –
          do not create a duplicate.
        </Alert>
      )}

      {vendor.status === 'PENDING' && (
        <Alert variant="warning" className="tw-mb-3">
          This vendor is <strong>pending approval</strong>. Resources can be added now.
          {activationBlockers.length > 0 && (
            <>
              {' '}Before it can be activated:
              <ul className="tw-mb-0 tw-mt-1 tw-pl-4">
                {activationBlockers.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </>
          )}
        </Alert>
      )}

      {/* M14c: one compliance banner – a warning, not a stop. Activation (M14b) is unchanged and
          is covered by the pending banner above, so it is not repeated for a pending vendor. */}
      {vendor.status !== 'PENDING' && complianceItems.length > 0 && (
        <Alert variant="warning" className="tw-mb-3" data-testid="vendor-compliance-banner">
          {complianceBannerText(complianceItems)}
        </Alert>
      )}

      {vendor.status === 'PENDING' && (
        <PossibleMatches matches={vendor.possibleMatches} reason={vendor.duplicateOverrideReason} />
      )}

      {/*
        The vendor summary is a fixed card above the tabs, not a tab of its own: status,
        contact, service areas and compliance are the context you read the tabs against, so
        they stay on screen while you move between Resources & Rates, Documents and History.
      */}
      <Card className="tw-mb-3">
        <Card.Body>
          <Row>
            <Col md={3}>
              <Field label="Status">
                {isActive && <Badge bg="success">Active</Badge>}
                {vendor.status === 'PENDING' && <Badge bg="warning">Pending approval</Badge>}
                {vendor.status === 'INACTIVE' && <Badge bg="secondary">Inactive</Badge>}
              </Field>
              <Field label="Legal Name">{vendor.legalName || '–'}</Field>
              <Field label="GST / Business Number">{vendor.gstBusinessNumber || '–'}</Field>
            </Col>
            <Col md={3}>
              <Field label="Contact Email">{vendor.contactEmail || '–'}</Field>
              <Field label="Phone">{vendor.phoneNumber || '–'}</Field>
              <Field label="Address">
                <span className="tw-whitespace-pre-line">{vendor.address || '–'}</span>
              </Field>
            </Col>
            <Col md={3}>
              <Field label="Service Areas">
                {vendor.serviceAreas?.map((sa) => sa.serviceArea.name).join(', ') || '–'}
              </Field>
              <Field label="Entered By">
                <span className={vendor.createdBy ? '' : 'tw-text-content-muted'}>{enteredByLabel(vendor)}</span>
              </Field>
              <Field label="WCB Account Number">{vendor.wcbAccountNumber || '–'}</Field>
            </Col>
            <Col md={3}>
              <Field label="Compliance">
                <ExpiryIndicator compliance={vendor.compliance} showLabel />
                {!vendor.compliance?.hasIssue && (
                  <span className="tw-text-success">
                    <i className="bi bi-check-circle tw-mr-1" />
                    WCB and liability insurance current
                  </span>
                )}
              </Field>
              <Field label="Approved By">
                {vendor.approvedBy
                  ? `${vendor.approvedBy.name} ${vendor.approvedBy.lastName} · ${shortDate(vendor.approvedAt)}`
                  : '–'}
              </Field>
              <Field label="ERP Vendor ID">{vendor.erpVendorId || '–'}</Field>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Tabs activeKey={activeTab} onSelect={handleTabSelect} id="vendor-detail-tabs">
        <Tab eventKey="resources" title={`Resources & Rates (${vendor.resources?.length ?? 0})`}>
          <div>
            {/*
              Add Resource lives here rather than in the page header: it only applies to this
              tab, and a header button that does nothing relevant on the Documents tab is
              worse than no button.
            */}
            {/*
              flex-wrap + min-w-0 are both load-bearing at 375px: without them the 260px
              filter and the Add Resource button sum past the viewport and the button is
              silently CLIPPED (the document does not scroll), putting part of it out of reach.
            */}
            <div className="tw-flex tw-flex-wrap tw-justify-between tw-items-end tw-gap-3 tw-mb-3">
              <div className="tw-min-w-0 tw-flex-1" style={{ maxWidth: '380px' }}>
                <MultiSelectDropdown
                  options={vendorAreaOptions}
                  value={resourceAreaIds}
                  onChange={(v) => setResourceAreaIds(v.map(Number))}
                  getValue={(o) => o.id}
                  formatOption={(o) => o.name}
                  placeholder="All Service Areas"
                  label="Service Area"
                  className="tw-w-full"
                  showSearch={vendorAreaOptions.length > 6}
                />
              </div>
              {canManageResources && inCallerArea && (
                <Button
                  variant="primary"
                  onClick={() => {
                    setEditingResource(null);
                    setShowResourceModal(true);
                  }}
                >
                  <i className="bi bi-plus-lg tw-mr-2" />
                  Add Resource
                </Button>
              )}
            </div>

            {/*
              Grouped by type, because the same machine is deliberately entered twice — once
              under the blue book and once as subcontractor, at different rates. In a
              flat list those two rows sit adjacent, look like a duplicate, and invite someone
              to "clean up" the one that is actually load-bearing.
            */}
            {visibleResources.length === 0 ? (
              <DataTable
                columns={resourceColumns}
                data={[]}
                rowKey={(r) => r.id}
                framed
                emptyMessage={
                  resourceAreaIds.length
                    ? 'No resources are offered in the selected service area(s).'
                    : 'No resources yet. Add hired equipment, subcontractor, materials, attachments or a bundle.'
                }
              />
            ) : (
              groupResourcesByType(visibleResources).map(({ group, resources }) => (
                <div key={group} className="tw-mb-4">
                  <h6 className="tw-mb-2 tw-text-sm tw-font-semibold tw-tracking-wide tw-text-content-secondary">
                    {group}{' '}
                    <span className="tw-font-normal tw-text-content-muted">({resources.length})</span>
                  </h6>
                  <DataTable
                    columns={resourceColumns}
                    data={resources}
                    rowKey={(r) => r.id}
                    framed
                    isExpanded={(r) => r.resourceType === 'BUNDLE' && expandedBundles.has(r.id)}
                    renderExpanded={(r) => <BundleComponentsList resource={r} />}
                    emptyMessage=""
                  />
                </div>
              ))
            )}
          </div>
        </Tab>

        <Tab eventKey="documents" title={`Documents (${vendor.documents?.length ?? 0})`}>
          <div>
            <VendorDocumentsPanel
              vendor={vendor}
              canManage={canManageDocs && inCallerArea}
              onChanged={load}
            />
          </div>
        </Tab>

        <Tab eventKey="history" title="History">
          <VendorHistoryTab vendorId={vendor.id} />
        </Tab>


      </Tabs>

      <VendorModal
        show={showVendorModal}
        onHide={() => setShowVendorModal(false)}
        vendor={vendor}
        onSave={handleSaveVendor}
      />

      <AddVendorToAreaModal
        show={showAddToArea}
        vendor={vendor}
        callerAreas={callerAreas}
        onHide={() => setShowAddToArea(false)}
        onAdded={async (updated) => {
          setShowAddToArea(false);
          setVendor(updated);
        }}
      />

      <ResourceModal
        show={showResourceModal}
        onHide={() => setShowResourceModal(false)}
        vendor={vendor}
        resource={editingResource}
        onSaved={async () => {
          setShowResourceModal(false);
          await load();
        }}
      />

      <RateModal
        show={!!rateTarget}
        onHide={() => setRateTarget(null)}
        vendor={vendor}
        resource={rateTarget}
        onSaved={async () => {
          await load({ silent: true });
        }}
      />
    </div>
  );
};

export default VendorDetailPage;
